// OverallAnalytics.js
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import EnrollmentBarChart from "./Charts/EnrollmentBarChart";
import EnrollmentLineGraph from "./Charts/EnrollmentLineGraph";
import MajorPercentageBarChart from "./Charts/MajorDistributionBarChart";
import AcademicYearChart from "./Charts/AcademicYearChart";
import GradeDistributionChart from "./Charts/GradeDistributionChart";
import InsightsComponent from "./InsightsComponent";

const OverallAnalytics = ({ analyticsData, activeChartIndex, setActiveChartIndex }) => {
  const chartComponents = [
    {
      title: "Enrollment + Forecasted Demand",
      component: <EnrollmentBarChart data={analyticsData?.enrollmentData} />,
    },
    {
      title: "Enrollment Trends Over Time",
      component: <EnrollmentLineGraph data={analyticsData?.enrollmentData} />,
    },
    {
      title: "Major Distribution",
      component: <MajorPercentageBarChart data={analyticsData?.majorEnrollmentData} />,
    },
    {
      title: "Academic Year Distribution",
      component: <AcademicYearChart data={analyticsData?.yearEnrollmentData} />,
    },
    {
      title: "Grade Distribution",
      component: <GradeDistributionChart data={analyticsData?.gradeData} />,
    },
  ];

  return (
    <>
      {/* Insights Section */}
      <InsightsComponent
        analyticsData={analyticsData}
        activeChartIndex={activeChartIndex}
      />

      <div className="flex gap-4">
        {/* Chart Section */}
        <div className="w-2/3">
          <Swiper
            modules={[Navigation, Pagination]}
            navigation
            pagination={{ clickable: true }}
            className="w-full h-[60vh] p-2 border-2 border-gray-200 rounded-md"
            onSlideChange={(swiper) => setActiveChartIndex(swiper.activeIndex)}
          >
            {chartComponents.map((chart, index) => (
              <SwiperSlide key={index}>
                <div className="flex flex-col items-start rounded-md">
                  <h3 className="text-lg ml-2 font-semibold text-gray-700 mb-1 self-start">
                    {chart.title}
                  </h3>
                  <div className="w-full h-[45vh]">{chart.component}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <style jsx>{`
            /* Adjust the position of navigation buttons */
            .swiper-button-next,
            .swiper-button-prev {
              top: 50%;
              transform: translateY(-50%);
              width: 20px;
              height: 20px;
              margin-top: 0;
              color: #4b5563;
              transition: box-shadow 0.3s ease;
            }
            .swiper-button-next {
              right: 10px;
            }
            .swiper-button-prev {
              left: 10px;
            }
          `}</style>
        </div>

        {/* Trends Section (stacked vertically) */}
        <div className="w-1/3 flex flex-col gap-4">
          {["fall", "spring", "summer"].map((key) => {
            const keyLower = key.toLowerCase();
            return (
              <div key={key} className="px-3 py-2 border-2 border-gray-200 rounded-md">
                <h3 className="text-lg font-semibold capitalize mb-2">
                  {key} Enrollment Trends
                </h3>
                <div className="space-y-3 text-md mb-2">
                  <div className="flex items-center">
                    <span
                      className={`${
                        analyticsData?.trends?.[key]?.historical >= 0
                          ? "bg-green-100 text-green-500"
                          : "bg-red-100 text-red-500"
                      } font-bold rounded-md text-sm w-[45px] py-0.5 text-center mr-2`}
                    >
                      {analyticsData?.trends?.[key]?.historical >= 0 ? "+" : ""}
                      {analyticsData?.trends?.[key]?.historical}%
                    </span>
                    <p className="text-gray-600 text-sm">
                      Historical Trend (
                      {keyLower === "fall"
                        ? "FA21 - FA23"
                        : keyLower === "spring"
                        ? "SP21 - SP24"
                        : "SU21 - SU24"}
                      )
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span
                      className={`${
                        analyticsData?.trends?.[key]?.forecasted >= 0
                          ? "bg-green-100 text-green-500"
                          : "bg-red-100 text-red-500"
                      } font-bold rounded-md text-sm w-[45px] py-0.5 text-center mr-2`}
                    >
                      {analyticsData?.trends?.[key]?.forecasted >= 0 ? "+" : ""}
                      {analyticsData?.trends?.[key]?.forecasted}%
                    </span>
                    <p className="text-gray-600 text-sm">
                      Forecasted Trend (
                      {keyLower === "fall"
                        ? "FA24"
                        : keyLower === "spring"
                        ? "SP25"
                        : "SU25"}
                      )
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default OverallAnalytics;
